import { Col, Container, Row } from "react-bootstrap";
import { Button, IconMinus, IconPlus, Table, TextBox, Tabs, Search } from '@veneer/core';
import React, { useState, useEffect } from 'react';
import './bulk-upload-tabs.css';
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { apiGet, apiGetWithHeader } from "@services/service";
import { GET_RECENT_BULK_SUBMISSIONS } from "services";
import Exporttoexcel from "../case-homepage/cases/exporttoexcel";

interface TableRow {
    id: number;
    [key: string]: any;
}

const BulkUploadStatus: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [tableData, setTableData] = useState<TableRow[]>([]);
    const [pageLoad, setPageLoad] = useState<TableRow[]>([]);
    const [exportXL, setExportXL] = useState<TableRow[]>([]);
    const [progress, setProgress] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const pagination = [{}];

    // Render function for CaseNumber hyperlink
    const renderCaseNumber = (data: any) => {
        if (!data.CaseNumber) {
            return data.CaseNumber;
        }
        let uri = `cid=${data.CaseId}`;
        return (
            <button
                className="link"
                onClick={() => {
                    navigate(`/edit-case?${uri}`, { state: { data } });
                }}
            >
                {data.CaseNumber}
            </button>
        );
    };

    // Helper function to format date
    const formatDateTime = (dateString: string): string => {
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString; // Return original if invalid date
            }
            
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        } catch (error) {
            return dateString; // Return original string on error
        }
    };

    // Search function similar to recentcases
    const handleSearch = (value: string | undefined, field: string) => {
        if (!value) {
            setTableData(pageLoad);
            return;
        }
        
        const filtered = pageLoad.filter((item: any) => {
            return item?.[field]
                ?.toString()
                ?.toLowerCase()
                ?.includes(value.trim().toLowerCase());
        });
        setTableData(filtered);
    };

    // Function to set filtered table data
    const setTableFliterData = (data: TableRow[]) => {
        setTableData(data);
    };

    const getbulkUploadStatus = async () => {
        try {
            // Set progress to true before API call
            setProgress(true);
            
            // Call API to get bulk upload status and update tableData state
            const response = await apiGetWithHeader(GET_RECENT_BULK_SUBMISSIONS);
            
            if (response?.data?.submissions) {
                // Format the UpdatedAt field for each row
                const formattedData = response.data.submissions.map((row: any) => ({
                    ...row,
                    UpdatedAt: row.UpdatedAt ? formatDateTime(row.UpdatedAt) : row.UpdatedAt
                }));
                setPageLoad(formattedData);
                setTableData(formattedData);
                let exportData = formattedData.map(({UpdatedAt, ...rest}: any) => ({
                    "Case Id": rest.CaseNumber, // Set CaseNumber as the first column without a header
                    "Status": rest.Status,
                    "Serial Number": rest.SerialNumber,
                    "Product Number": rest.ProductNumber,
                    "Case Title": rest.CaseTitle,
                    "Error Message": rest.ErrorMessage,
                    "Updated At": UpdatedAt ? formatDateTime(UpdatedAt) : UpdatedAt
                }));
                setExportXL(exportData);    
            } else {
                setPageLoad([]);
                setTableData([]);
            }
            
            // Set progress to false after successful API call
            setProgress(false);
        } catch (error) {
            // Set progress to false in case of error
            setProgress(false);
            // Set empty array in case of error
            setPageLoad([]);
            setTableData([]);
        }
    }
    useEffect(() => {
        // Initial data fetch
        getbulkUploadStatus();
        
        // Set up polling every 10 seconds (10000 milliseconds)
        const intervalId = setInterval(() => {
            getbulkUploadStatus();
        }, 20000);
        
        // Cleanup interval on component unmount
        return () => {
            clearInterval(intervalId);
        };
    }, []);

    // Reset current page when pagination is not available
    useEffect(() => {
        if (!pagination) {
            setCurrentPage(1);
        }
    }, [pagination]);

    const columns = [
        {
            id: "CaseNumber", 
            label: t("HPI.BulkUploadStatus.CaseId", { defaultValue: "Case Id" }),
            filter: (
                <Search
                    placeholder={t("HPI.ContractWarranties.btnSearch", {
                        defaultValue: "Search",
                    })}
                    onChange={(v) => handleSearch(v, "CaseNumber")}
                    onClear={() => setTableFliterData(pageLoad)}
                />
            ),
        },
        {
            id: "Status", 
            label: t("HPI.BulkUploadStatus.Status", { defaultValue: "Status" }),
            filter: (
                <Search
                    placeholder={t("HPI.ContractWarranties.btnSearch", {
                        defaultValue: "Search",
                    })}
                    onChange={(v) => handleSearch(v, "Status")}
                    onClear={() => setTableFliterData(pageLoad)}
                />
            ),
        },
        {
            id: "SerialNumber", 
            label: t("HPI.BulkUploadStatus.SerialNumber", { defaultValue: "Serial Number" }),
            filter: (
                <Search
                    placeholder={t("HPI.ContractWarranties.btnSearch", {
                        defaultValue: "Search",
                    })}
                    onChange={(v) => handleSearch(v, "SerialNumber")}
                    onClear={() => setTableFliterData(pageLoad)}
                />
            ),
        },
        {
            id: "ProductNumber", 
            label: t("HPI.BulkUploadStatus.ProductNumber", { defaultValue: "Product Number" }),
            filter: (
                <Search
                    placeholder={t("HPI.ContractWarranties.btnSearch", {
                        defaultValue: "Search",
                    })}
                    onChange={(v) => handleSearch(v, "ProductNumber")}
                    onClear={() => setTableFliterData(pageLoad)}
                />
            ),
        },

        {
            id: "CaseTitle",
            label: t("HPI.BulkUploadStatus.CaseTitle", { defaultValue: "Case Title" }),
            filter: (
                <Search
                    placeholder={t("HPI.ContractWarranties.btnSearch", {
                        defaultValue: "Search",
                    })}
                    onChange={(v) => handleSearch(v, "CaseTitle")}
                    onClear={() => setTableFliterData(pageLoad)}
                />
            ),
        },

        {
            id: "ErrorMessage", 
            label: t("HPI.BulkUploadStatus.ErrorMessage", { defaultValue: "Error Message" }),
            filter: (
                <Search
                    placeholder={t("HPI.ContractWarranties.btnSearch", {
                        defaultValue: "Search",
                    })}
                    onChange={(v) => handleSearch(v, "ErrorMessage")}
                    onClear={() => setTableFliterData(pageLoad)}
                />
            ),
        },
        // {
        //     id: "Status", 
        //     label: t("HPI.BulkUploadStatus.Status", { defaultValue: "Status" }),
        //     filter: (
        //         <Search
        //             placeholder={t("HPI.ContractWarranties.btnSearch", {
        //                 defaultValue: "Search",
        //             })}
        //             onChange={(v) => handleSearch(v, "Status")}
        //             onClear={() => setTableFliterData(pageLoad)}
        //         />
        //     ),
        // },
        {
            id: "UpdatedAt", 
            label: t("HPI.BulkUploadStatus.UpdatedAt", { defaultValue: "Date" }),
            filter: (
                <Search
                    placeholder={t("HPI.ContractWarranties.btnSearch", {
                        defaultValue: "Search",
                    })}
                    onChange={(v) => handleSearch(v, "UpdatedAt")}
                    onClear={() => setTableFliterData(pageLoad)}
                />
            ),
        },
    ];

    // Pagination logic similar to recentcases
    let pagedData = pagination
        ? tableData.slice(
            (currentPage - 1) * pageSize,
            (currentPage - 1) * pageSize + pageSize
        )
        : tableData;
    
    if (pagedData.length === 0) {
        pagedData = tableData;
    }

    // Transform data to include hyperlink for CaseNumber
    const transformedData = pagedData.map((row: any) => ({
        ...row,
        CaseNumber: renderCaseNumber(row)
    }));
    
    const paginationProps = pagination
        ? {
            currentPage,
            pageSize,
            onPageChange: (page: number) => {
                setCurrentPage(page);
            },
            totalItems: tableData.length,
        }
        : undefined;
    return (
        <>
            
            {progress ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            ) : (
                <>
                <div className="d-flex justify-content-end mb-4">
                    <Exporttoexcel fileName="BulkUploadCaseStatus" rows={exportXL} />
                </div>
                <Table 
                    columns={columns} 
                    showFilters
                    data={transformedData} 
                    pagination={paginationProps}
                />
                </>
            )}
            {/* <Row>
                <Col className="d-flex justify-content-end mt-4">
                    <div className="button-conatiner">
                        <Button
                            style={{ marginLeft: "20px" }}
                            type="button"
                            disabled={progress}
                        >
                            {"Re-process"}
                        </Button>
                    </div>
                </Col>
            </Row> */}
        </>
    );
};
export default BulkUploadStatus;
